import { IWorkflowStep, APP_TYPES, ATTRIBUTE_ISSUERS, suppliedCredentialTypes } from '@app/pages/ssoManager/addEditApplication.enums';
import { PROTOCOL_TYPES_VALUE } from '@app/pages/ssoManager/addEditApplication.enums';
import {emptyGuid, idForK1App} from '@app/helpers/constants';
import { IApplicationAttribute } from '@app/models';
import _ from 'lodash';
import { ApplicationInterfaceTypeUnion } from '../components/SSOApps/addEditAppTabs/appContext';

// WORKFLOW
export const loadWorkflowSuppliedCredential = (appAttributes: IApplicationAttribute[] | undefined): string => {
    if (_.find(appAttributes, { Key: 'suppliedCredential', Value: 'administrator' })) {
        return suppliedCredentialTypes.admin;
    }
    else {
        return suppliedCredentialTypes.user;
    }
};

export const generateWorkflowSteps = (workflowString: string): IWorkflowStep[] => {
    return JSON.parse(workflowString);
};

export const isWorkflowStepInput = (workflowStep: IWorkflowStep) => {
    return workflowStep.step === 'input';
};

export const isWorkflowStepPassword = (workflowStep: IWorkflowStep) => {
    return workflowStep.type.toUpperCase() === 'password'.toUpperCase();
};

// PROTOCOL
export const isSpInit = (protocol: string): boolean => protocol === PROTOCOL_TYPES_VALUE.spInit;
export const isIdPInit = (protocol: string): boolean => protocol === PROTOCOL_TYPES_VALUE.idpInit;
export const isWSFed = (protocol: string): boolean => protocol === PROTOCOL_TYPES_VALUE.wsFed;
export const isOAuth = (protocol: string): boolean => protocol === PROTOCOL_TYPES_VALUE.oAuth2;

export const isWorkflowApp = (app: any) => app['@odata.type'] === APP_TYPES.workflow || app['@odata.type'] === APP_TYPES.catalogWorkflow;
export const isCatalogApp = (app: any) => app['@odata.type'] === APP_TYPES.catalog;
export const isCustomApp = (app: any) => (app['@odata.type'] === APP_TYPES.catalogWorkflow || app['@odata.type'] === APP_TYPES.catalog || app.Id === emptyGuid);

// AUDIENCE URIS
export const additionalAudiencesPresent = (audienceUrisArray: string[], replyoUrl: string) => {
    return audienceUrisArray?.length > 1 || audienceUrisArray?.length ? audienceUrisArray[0] !== replyoUrl : false;
};

// TOKEN
export const tokenLifetimeToInt = (token: string) => {
    if (/PT([\d]+)M/.test(token)) {
        return +/PT([\d]+)M/.exec(token)![1];
    }
};

// ATTRIBUTE TRANSFORMATION
export const issueAsUsername = (selected: string) => selected === ATTRIBUTE_ISSUERS.username;
export const issueAsCustom = (selected: string) => selected === ATTRIBUTE_ISSUERS.custom;

// CERTIFICATE
export const generateCertificateFormData = (files: FileList): FormData => {
    const formData = new FormData();

    if (files?.length) {
        Array.from(files)
            .forEach((file: File) => {
                formData.append('file', file, file.name);
            });
    }

    return formData;
};

export const atoBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
    let byteCharacters = atob(b64Data);
    let byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

type authErrorTranslation = 'MSOL_AUTH_FAILURE' | 'MSOL_AUTH_ERROR' | 'MSOL_INVALID_USERNAME' | 'MSOL_IDENTITY_CRL_NOT_INSTALLED' | 'OFFICE365_MANAGED_DOMAIN_NOT_FOUND';

enum AUTH_ERRORS {
    FailAuthIdentityToService,
    FailCloseIdentityHandle,
    FailCreateIdentityHandle,
    FailGetAuthInvalidState,
    FailGetAuthState,
    FailInitializeEx,
    FailLoginIdentity,
    FailPassportFreeMemory,
    FailSetCredential,
    FailUninitialize,
    FailMustByOnmicrosoftAccount,
    MsolIdentityCrlNotInstalled,
    Office365ManagedDomainNotFound
}

type AUTH_ERRORS_KEYS = keyof typeof AUTH_ERRORS;

type IAuthErrors = {
    [key in AUTH_ERRORS_KEYS | string]: authErrorTranslation
};

export const tryTranslateError = (results: any): string | authErrorTranslation => {
    let authErrors: IAuthErrors = {
        FailAuthIdentityToService: 'MSOL_AUTH_FAILURE',
        FailCloseIdentityHandle: 'MSOL_AUTH_ERROR',
        FailCreateIdentityHandle: 'MSOL_AUTH_ERROR',
        FailGetAuthInvalidState: 'MSOL_AUTH_FAILURE',
        FailGetAuthState: 'MSOL_AUTH_FAILURE',
        FailInitializeEx: 'MSOL_AUTH_ERROR',
        FailLoginIdentity: 'MSOL_AUTH_FAILURE',
        FailPassportFreeMemory: 'MSOL_AUTH_ERROR',
        FailSetCredential: 'MSOL_AUTH_ERROR',
        FailUninitialize: 'MSOL_AUTH_ERROR',
        FailMustByOnmicrosoftAccount: 'MSOL_INVALID_USERNAME',
        MsolIdentityCrlNotInstalled: 'MSOL_IDENTITY_CRL_NOT_INSTALLED',
        Office365ManagedDomainNotFound: 'OFFICE365_MANAGED_DOMAIN_NOT_FOUND'
    };

    let errorKey = null;

    Object.keys(authErrors).forEach((error: string) => {
        if (results.indexOf(error) >= 0) {
            errorKey = authErrors[error];
        }
    });

    if (errorKey) {
        return errorKey;
    }

    return results;
};

export const translateMsol = (name: string) => {
    const prefixed = `MSOL_${name}`;

    const translated = (prefixed);

    if (translated !== prefixed) {
        return translated;
    }

    return name;
};

export const isTemplate = (app: ApplicationInterfaceTypeUnion) => {
    return app?.ServiceConfiguration ? Boolean(JSON.parse(app.ServiceConfiguration).template) : false;
};

export const isKaseyaOne = (ServiceConfiguration: string) => {
    const aid = ServiceConfiguration ? JSON.parse(ServiceConfiguration).Aid : '';

    return aid === idForK1App;
};
